import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useContext } from 'react';

import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import AppContext from '@/contexts/appContext';
import PersonContext from '@/pages/Person/PersonContext';
import PersonRepository from '@/repositories/PersonRepository';
import type { Person } from '@/types/person';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import type { PersonInPlaceEditorProps as Props } from './types';

const PersonInPlaceEditor = ({
  children,
  disableEdit,
  fields,
  schema,
  sectionEditor,
  subsectionEditor
}: Props) => {
  const repository = PersonRepository();
  const { addMessage } = useContext(AppContext);
  const { person: personContext, updatePerson: refetchPerson } = useContext(PersonContext);

  const updatePerson = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Partial<Person> }) => {
      if (!id) {
        throw new Error('Person ID is required');
      }

      return repository.updatePerson({ id, values });
    },
    onSuccess: async (response) => {
      if (!response) return;

      await refetchPerson();
      addMessage('success', 'Profile updated successfully');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  return (
    <InPlaceEditor
      schema={schema}
      disableEdit={disableEdit}
      fields={fields}
      record={personContext}
      mutation={updatePerson}
      sectionEditor={sectionEditor}
      subsectionEditor={subsectionEditor}>
      {children}
    </InPlaceEditor>
  );
};
export default memo(PersonInPlaceEditor);
